/* eslint-disable @typescript-eslint/no-floating-promises */
import React, { useEffect, useMemo } from 'react';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import type { GetServerSideProps } from 'next';

import { validate as uuidValidate } from 'uuid';

import Container from '@mui/material/Container';
import CardMedia from '@mui/material/CardMedia';
import Box from '@mui/material/Box';

import { useTranslation } from 'next-i18next';

import useAppRouter from 'src/hooks/useAppRouter';

// Components
import Image from 'src/components/ui/Image';
import Tag from 'src/components/ui/Tag';
import PageNotFound from 'src/pages/404';
import Link from 'src/components/ui/Link';
import Icon from 'src/components/ui/Icon';
import Loader from 'src/components/ui/Loader';
import type { Props as TypographyProps } from 'src/components/ui/Typography';
import Typography from 'src/components/ui/Typography';
import AspectRatio from 'src/components/ui/AspectRatio';
import MediaCarousel from 'src/components/ui/MediaCarousel';
import FullWidthLayout from 'src/components/other/FullWidthLayout';
import NewsPostHeader from 'src/components/news-post/NewsPostHeader';

// Data
import { useNewsPostQuery, useIncrementStatsViewCountMutation } from 'src/hooks/__generated__/queries';
import type { Medium, NewsPostQuery } from 'src/types/__generated__/graphql';
import { NewsPost } from 'src/types/__generated__/graphql';

// Constants
import { colours } from 'src/constants/colours';
import { breakpoints } from 'src/constants/breakpoints';
import { analyseMedia } from 'src/utils/analyseMedia';
import { defaultAudioImagePath } from 'src/constants/defaultImages';
import { useSsrWindowSize } from 'src/hooks/useSsr';
import { getAbsoluteUrl } from 'src/utils/formatUrl';
import { removeAllHTMLTags } from 'src/utils/sanitizeHtml';
import { styledComponent } from 'src/utils/styled';
import { getAuthCookie } from 'src/utils/authCookie';
import getApolloClient from 'src/utils/getApolloClient';
import { EventParamKeys } from 'src/utils/analytics';
import { AnalyticsContextProvider } from 'src/hooks/useDynamicLogEvents';

const StyledTag = styledComponent(Tag)`
    padding: 0.1875rem 0;
    height: fit-content;
    margin-bottom: 0.3rem;
    margin-left: 0.3rem;
    background-color: ${colours.secondary.O08};
    color: ${colours.secondary.main};
    img {
        width: 1rem;
        height: 1rem;
    }
    span {
        font-weight: 400;
    }
`;

const StyledContainer = styledComponent(Container)<{ $isDesktop: boolean }>`
    display: flex;
    flex-direction: column;
    margin-bottom: 3rem;
    ${({ $isDesktop }) => $isDesktop && ` padding-right: 9%; padding-left: 9%;`}

    .gallery {
        @media (max-width: ${breakpoints.tabletPortrait - 1}px) {
            margin: 0 -1rem;
        }
    }
`;

const StyledViewAndLikeBox = styledComponent(Box)`
    padding: 0.2rem 0.5rem;
    vertical-align: middle;
`;

const StyledBox = styledComponent(Box)`
    img {
        display: flex;
        margin-left: auto;
        margin-right: auto;
        max-width: 100%;
    }
`;

interface StyledTyprographyProps extends TypographyProps {
    component?: string;
}
const StyledTypography = styledComponent(Typography)<StyledTyprographyProps>``;

const captureNewsPostContentClicks = (ev: React.MouseEvent<HTMLElement>) => {
    let target: HTMLElement | null = ev.target as HTMLElement;

    // Crawl up dom tree, look if click landed inside a <a /> tag
    while (target && target.tagName !== 'A') {
        target = target.parentElement;
    }

    // If <a /> tag found
    if (target) {
        ev.preventDefault();
        window.open(target.getAttribute('href') as string, '_blank', 'noopener');
    }
};

const NewsPostPage: PageComponent = () => {
    const { t, i18n } = useTranslation(['ad', 'common']);
    const router = useAppRouter();
    const { width } = useSsrWindowSize();
    const [incrementStatsViewCount] = useIncrementStatsViewCountMutation();

    const newsPostId = router.query.newsPostId as string;

    const { data: newsPostData } = useNewsPostQuery({
        variables: {
            newsPostId: newsPostId as string,
        },
        skip: !newsPostId || !uuidValidate(newsPostId),
        onCompleted(data) {
            if (data.newsPost) {
                incrementStatsViewCount({
                    variables: {
                        newsPostId: data.newsPost.id,
                    },
                });
            }
        },
    });

    const analyticsValue = useMemo(
        () => ({
            [EventParamKeys.ItemId]: newsPostId,
            [EventParamKeys.ItemName]: newsPostData?.newsPost?.title ?? undefined,
        }),
        [newsPostData?.newsPost?.title, newsPostId],
    );

    if (newsPostData && !newsPostData.newsPost) {
        return <PageNotFound />;
    }

    const newPostLinkAfterMatchLink = String(newsPostData?.newsPost?.newsPostLink).match(
        // eslint-disable-next-line no-useless-escape
        /^(?:https?:\/\/)?(?:[^@\/\n]+@)?(?:www\.)?([^:\/?\n]+)/gim,
    );

    return newsPostData?.newsPost ? (
        <AnalyticsContextProvider value={analyticsValue}>
            <StyledContainer $isDesktop={!width || breakpoints.tabletPortrait < width}>
                <NewsPostHeader newsPost={newsPostData.newsPost} />
                <Box py={3} display="flex">
                    <Typography variant="h1" data-target="news-post" data-target-id="news-post-title">
                        {newsPostData.newsPost.title}
                    </Typography>
                </Box>

                {newsPostData.newsPost.media.length > 0 && (
                    <CardMedia>
                        <AspectRatio ratio={1.77}>
                            <MediaCarousel
                                media={newsPostData.newsPost.media as Medium[]}
                                alt={String((newsPostData.newsPost as NewsPost).title)}
                                objectFit="contain"
                                globalAudioTitle={newsPostData.newsPost.title ?? undefined}
                                globalAudioContent={{
                                    type: 'newsPost',
                                    id: newsPostId,
                                }}
                                fixedArrows
                                useGlobalAudio
                            />
                        </AspectRatio>
                    </CardMedia>
                )}

                <Box my={2} display="flex" flexDirection="row" alignItems="center">
                    {newsPostData.newsPost.featuredCategory && (
                        <StyledTag
                            key={`${newsPostData.newsPost.id}-${newsPostData.newsPost.featuredCategory.id}`}
                            data-target="news-post"
                            data-target-id="news-post-tag"
                            label={
                                i18n.language === 'fr'
                                    ? newsPostData.newsPost.featuredCategory.nameFr
                                    : newsPostData.newsPost.featuredCategory.nameEn
                            }
                            icon={
                                <Image
                                    src={newsPostData.newsPost.featuredCategory.icon}
                                    alt={newsPostData.newsPost.featuredCategory.nameFr}
                                    width={16}
                                    height={16}
                                />
                            }
                        />
                    )}

                    <StyledViewAndLikeBox display="flex" flexDirection="row">
                        <Box pl={1} display="flex" flexDirection="row" alignItems="center">
                            <Box mr={0.5} display="flex" flexDirection="column" justifyContent="center">
                                <Icon name="Show" size="1rem" />
                            </Box>
                            <Typography data-target="news-post" data-target-id="news-post-views">
                                {newsPostData.newsPost.stats.viewCount}
                            </Typography>
                        </Box>

                        <Box pl={1} display="flex" flexDirection="row" alignItems="center">
                            <Box mr={0.5} display="flex" flexDirection="column" justifyContent="center">
                                <Icon name="HeartFillGrey" size="1rem" />
                            </Box>
                            <Typography
                                color="primary"
                                variant="subtitle1"
                                data-target="news-post"
                                data-target-id="news-post-likes"
                            >
                                {newsPostData.newsPost.totalLikes}
                            </Typography>
                        </Box>
                    </StyledViewAndLikeBox>
                </Box>

                <Box onClick={captureNewsPostContentClicks}>
                    <StyledBox>
                        <StyledTypography
                            component="span"
                            color="primary"
                            variant="body1"
                            data-target="news-post"
                            data-target-id="news-post-description"
                        >
                            <div
                                dangerouslySetInnerHTML={{
                                    __html: newsPostData.newsPost.description as string,
                                }}
                            />
                        </StyledTypography>
                    </StyledBox>
                </Box>

                <Box display="flex" flexDirection="row" alignItems="center" flexWrap="wrap">
                    {newsPostData.newsPost.categories?.map((category) => (
                        <StyledTag
                            key={`${newsPostData.newsPost?.id}-${category.id}`}
                            label={i18n.language === 'fr' ? category.nameFr : category.nameEn}
                            icon={<Image src={category.icon} alt={category.nameFr} width={16} height={16} />}
                        />
                    ))}
                </Box>

                <Box display="flex" flexDirection="row" my={2} color={colours.grey.G500}>
                    <Typography variant="body2">{t('common:source')}</Typography>
                    <Box ml={0.5}>
                        {newsPostData.newsPost.newsPostLink ? (
                            <Link
                                href={getAbsoluteUrl(newsPostData.newsPost.newsPostLink)}
                                data-target="news-post"
                                data-target-id="news-post-source-link"
                                openInNewTab
                            >
                                <Typography variant="body2">{newPostLinkAfterMatchLink}</Typography>
                            </Link>
                        ) : (
                            <Typography
                                variant="body2"
                                data-target="news-post"
                                data-target-id="news-post-source-woopen"
                            >
                                Woopen
                            </Typography>
                        )}
                    </Box>
                </Box>
            </StyledContainer>
        </AnalyticsContextProvider>
    ) : (
        <Loader />
    );
};

NewsPostPage.Layout = FullWidthLayout;

interface NewsPostPageProps extends PageProps {
    newsPost: NewsPostQuery['newsPost'];
}

export const getServerSideProps: GetServerSideProps<NewsPostPageProps> = async (context) => {
    const auth = getAuthCookie(context);
    const apolloClient = getApolloClient(null, auth);
    const newsPostId = context.params?.newsPostId as string | undefined;
    let data;
    if (newsPostId) {
        const result = await apolloClient.query<NewsPostQuery>({
            query: NewsPost,
            variables: {
                newsPostId,
            },
        });
        data = result.data;
    }

    const metaDescription = (
        data?.newsPost?.shortDescription && data.newsPost.shortDescription.length > 0
            ? data.newsPost.shortDescription
            : data?.newsPost?.description ?? ' '
    ).substring(0, 200);

    const metaDescriptionWithoutHtmlTags = removeAllHTMLTags(metaDescription);
    let imageUrl = null;
    const { image, video, audio } = data?.newsPost?.media
        ? analyseMedia(data.newsPost.media as Medium[])
        : { image: undefined, video: undefined, audio: undefined };
    if ((image && video && audio) || (image && video && !audio) || (image && !video && !audio)) {
        imageUrl = image.url;
    } else if ((!image && !video && audio) || (!image && video && audio)) {
        imageUrl = defaultAudioImagePath;
    }

    return {
        props: {
            newsPost: data?.newsPost,
            ...(context.locale
                ? await serverSideTranslations(context.locale, ['common', 'ad', 'news', 'favorites', 'contacts'])
                : {}),
            SEOProps: {
                pageTitleTranslation: {
                    value: String(data?.newsPost?.title),
                },
                metaTitleTranslation: {
                    value: String(data?.newsPost?.title),
                },
                metaDescriptionTranslation: {
                    value: metaDescriptionWithoutHtmlTags,
                },
                imageUrl,
                nofollow: true,
            },
        },
    };
};

export default NewsPostPage;
